
/* ========================================= */
/* src/pages/ProductDetail.tsx - DETALLE DE PRODUCTO */
/* ========================================= */

import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, ShoppingCart, Download, Star, Plus, Minus, Heart, Share2 } from 'lucide-react';
import { products } from '../data/products';
import { useCart } from '../contexts/CartContext';

const ProductDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { addItem } = useCart();
  const [quantity, setQuantity] = useState(1);
  const [activeTab, setActiveTab] = useState<'description' | 'specifications' | 'reviews'>('description');
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const product = products.find(p => p.id === id);

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-800 mb-4">Producto no encontrado</h1>
          <Link to="/productos" className="btn-primary">
            Volver a productos
          </Link>
        </div>
      </div>
    );
  }

  // Usar solo la imagen principal
  const images = [product.image];

  const handleAddToCart = () => {
    addItem(product, quantity);
    // Aquí podrías mostrar una notificación
  };

  const incrementQuantity = () => {
    if (typeof product.inStock === 'number' && quantity < product.inStock) {
      setQuantity(quantity + 1);
    }
  };

  const decrementQuantity = () => {
    if (quantity > 1) {
      setQuantity(quantity - 1);
    }
  };

  return (
    <div className="min-h-screen bg-infinity-black">
      {/* Breadcrumb */}
      <div className="bg-infinity-dark border-b border-infinity-yellow">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center space-x-2 text-sm">
            <Link to="/" className="text-infinity-white/60 hover:text-infinity-yellow">Inicio</Link>
            <span className="text-infinity-yellow">/</span>
            <Link to="/productos" className="text-infinity-white/60 hover:text-infinity-yellow">Productos</Link>
            <span className="text-infinity-yellow">/</span>
            <span className="text-infinity-yellow font-medium">{product.name}</span>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <Link
          to="/productos"
          className="inline-flex items-center text-infinity-yellow hover:text-infinity-white mb-6"
        >
          <ArrowLeft size={20} className="mr-2" />
          Volver a productos
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Galería de Imágenes */}
          <div>
            {/* Imagen Principal */}
            <div className="mb-4">
              <div className="aspect-square bg-infinity-yellow rounded-lg overflow-hidden">
                <img 
                src={product.image} 
                alt={product.name} 
                className="w-full h-full object-contain" />
              </div>
            </div>

            {/* Miniaturas: solo una imagen principal */}
            {images.length > 1 && (
              <div className="flex space-x-2 overflow-x-auto">
                {images.map((image: string, index: number) => (
                  <button
                    key={index}
                    onClick={() => setCurrentImageIndex(index)}
                    className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 ${
                      currentImageIndex === index ? 'border-infinity-yellow' : 'border-infinity-dark'
                    }`}
                  >
                    <img
                      src={image}
                      alt={`${product.name} - Vista ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
            {/* Advertencia de stock */}
            {typeof product.inStock === 'number' && product.inStock < 10 && (
              <p className="text-infinity-yellow text-xs mt-2">
                Solo quedan {product.inStock} unidades
              </p>
            )}
          </div>

          {/* Información del Producto */}
          <div>
            <div className="mb-4">
              {product.rating && (
                <div className="flex items-center mb-2">
                  <div className="flex text-naylamp-yellow">
                    {[...Array(5)].map((_, i) => (
                      <Star 
                        key={i} 
                        size={20} 
                        fill={i < Math.floor(product.rating!) ? "currentColor" : "none"} 
                      />
                    ))}
                  </div>
                  <span className="text-gray-500 ml-2">({product.rating})</span>
                </div>
              )}
              
              <h1 className="text-3xl font-bold text-infinity-yellow mb-4">{product.name}</h1>
              <p className="text-infinity-white/80 text-lg mb-6">{product.description}</p>
            </div>

            <div className="mb-6">
              <div className="text-4xl font-bold text-infinity-yellow mb-2">
        
              </div>
              <p className="text-green-400 font-medium">
                ✓ En stock ({typeof product.inStock === 'number' ? product.inStock : 'N/A'} disponibles)
              </p>
            </div>

            {/* Selector de Cantidad */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Cantidad
              </label>
              <div className="flex items-center space-x-4">
                <div className="flex items-center border border-infinity-yellow rounded-lg">
                  <button
                    onClick={decrementQuantity}
                    disabled={quantity <= 1}
                    className="p-2 hover:bg-infinity-yellow/20 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Minus size={16} />
                  </button>
                  <span className="px-4 py-2 font-medium text-infinity-yellow">{quantity}</span>
                  <button
                    onClick={incrementQuantity}
                    disabled={typeof product.inStock === 'number' && quantity >= product.inStock}
                    className="p-2 hover:bg-infinity-yellow/20 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Plus size={16} />
                  </button>
                </div>
                <span className="text-infinity-white/80">
                  Total: {quantity}
                </span>
              </div>
            </div>

            {/* Botones de Acción */}
            <div className="space-y-4 mb-8">
              <button
                onClick={handleAddToCart}
                disabled={typeof product.inStock === 'number' && product.inStock === 0}
                className="w-full btn-primary flex items-center justify-center space-x-2 text-lg py-4 bg-infinity-yellow text-infinity-black hover:bg-infinity-purple hover:text-infinity-white disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ShoppingCart size={20} />
                <span>Agregar al Carrito</span>
              </button>
              
              <div className="flex space-x-4">
                <button className="flex-1 btn-secondary flex items-center justify-center space-x-2 bg-infinity-dark text-infinity-yellow hover:bg-infinity-yellow hover:text-infinity-black">
                  <Heart size={16} />
                  <span>Favoritos</span>
                </button>
                <button className="flex-1 btn-secondary flex items-center justify-center space-x-2 bg-infinity-dark text-infinity-yellow hover:bg-infinity-yellow hover:text-infinity-black">
                  <Share2 size={16} />
                  <span>Compartir</span>
                </button>
              </div>
            </div>

            {/* Descargas */}
            {product.manuals && product.manuals.length > 0 && (
              <div className="border-t pt-6">
                <h3 className="text-lg font-semibold mb-3">Documentación</h3>
                <div className="space-y-2">
                  {product.manuals.map((manual, index) => (
                    <a
                      key={index}
                      href={manual}
                      className="flex items-center space-x-2 text-infinity-yellow hover:text-infinity-white"
                    >
                      <Download size={16} />
                      <span>Manual técnico PDF</span>
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Pestañas de Información */}
        <div className="mt-16">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8">
              {[
                { id: 'description', label: 'Descripción' },
                { id: 'specifications', label: 'Especificaciones' },
                { id: 'reviews', label: 'Opiniones' }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === tab.id
                      ? 'border-infinity-yellow text-infinity-yellow'
                      : 'border-transparent text-infinity-white/60 hover:text-infinity-yellow'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          <div className="py-8">
            {activeTab === 'description' && (
              <div className="prose max-w-none">
                <p className="text-infinity-white/80 leading-relaxed">
                  {product.description}
                </p>
                {/* Aquí puedes agregar más contenido descriptivo */}
              </div>
            )}

            {activeTab === 'specifications' && (
              <div>
                {product.specifications ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {Object.entries(product.specifications).map(([key, value]) => (
                      <div key={key} className="bg-infinity-dark p-4 rounded-lg">
                        <dt className="font-medium text-infinity-yellow">{key}</dt>
                        <dd className="text-infinity-white/80">{value}</dd>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-infinity-white/60">No hay especificaciones disponibles.</p>
                )}
              </div>
            )}

            {activeTab === 'reviews' && (
              <div>
                <p className="text-infinity-white/60">Las opiniones de clientes estarán disponibles próximamente.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;